import type { ReactNode } from 'react';

/**
 * Card surface. DESIGN.md §8.1 — soft shadow, no border.
 *
 * For the screen's hero card use {@link HeroCard} instead.
 */
export function Card({
  title,
  hint,
  children,
  className,
}: {
  title?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={
        'rounded-md bg-card p-5 shadow-sm transition-shadow hover:shadow-md ' +
        (className ?? '')
      }
    >
      {(title || hint) && (
        <header className="mb-4 flex items-baseline justify-between">
          {title && (
            <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-fg-muted">
              {title}
            </h2>
          )}
          {hint && <span className="text-xs text-fg-muted">{hint}</span>}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * Inline metric block — small, one-of-many on a card.
 * For the *hero* metric of a screen use {@link HeroMetric}.
 */
export function StatBlock({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'positive' | 'negative' | 'neutral';
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-positive'
      : tone === 'negative'
      ? 'text-negative'
      : tone === 'neutral'
      ? 'text-neutral'
      : '';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-[0.06em] text-fg-muted">{label}</span>
      <span className={`tnum text-2xl font-semibold ${toneClass}`}>{value}</span>
      {sub && <span className="text-xs text-fg-muted">{sub}</span>}
    </div>
  );
}
